// E2E Status Menu - Two-Panel Layout (Test List + Details)
import 'highlight.js/styles/github-dark.css';

import { Button } from '@praetorian-chariot/ui';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import {
  Bot,
  ChevronDown,
  ChevronRight,
  Copy,
  Pencil,
  Play,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';

// Register the language
hljs.registerLanguage('javascript', javascript);

// Helper function to strip ANSI color codes and convert to HTML
const formatErrorText = (text: string) => {
  if (!text) return '';

  // Strip ANSI color codes and convert to readable format
  const strippedText = text
    .replace(/\u001b\[[0-9;]*m/g, '') // Remove ANSI escape codes
    .replace(/\\n/g, '\n') // Convert literal \n to actual newlines
    .replace(/\\t/g, '    '); // Convert tabs to spaces

  return strippedText;
};

// Component for syntax-highlighted error display
const ErrorDisplay = ({ content }: { content: string }) => {
  const formattedContent = formatErrorText(content);

  // Apply syntax highlighting for test errors and code
  const getHighlightedContent = () => {
    try {
      // Try to detect if this looks like test error output
      if (
        formattedContent.includes('expect(') ||
        formattedContent.includes('Locator:') ||
        formattedContent.includes('getByRole(')
      ) {
        // For Playwright test errors, highlight as JavaScript
        const highlighted = hljs.highlight(formattedContent, {
          language: 'javascript',
        });
        return { __html: highlighted.value };
      }

      // For general error text, try auto-detection
      const highlighted = hljs.highlightAuto(formattedContent);
      return { __html: highlighted.value };
    } catch (error) {
      // Fallback to plain text if highlighting fails
      return null;
    }
  };

  const highlightedContent = getHighlightedContent();

  return (
    <div>
      <div className="bg-chariot-background-tertiary border-chariot-stroke rounded-lg border">
        <div className="p-4">
          {highlightedContent ? (
            <pre
              className="hljs !bg-transparent font-mono text-xs leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={highlightedContent}
            />
          ) : (
            <pre className="text-chariot-text-primary font-mono text-xs leading-relaxed whitespace-pre-wrap">
              {formattedContent}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

interface TestFailure {
  id: string;
  testName: string;
  spec: string;
  error: string;
  fullError: string;
  stackTrace: string;
  errorDetails: {
    shortError: string;
    fullError: string;
    stackTrace: string;
    errorName: string;
    errorMessage: string;
    codeFrame?: any;
    testLocation?: any;
    attempts: any[];
    annotations: any[];
    duration: number;
    timeout: number;
    signature: string;
    isFlaky: boolean;
    tags: string[];
  };
  instanceUrl: string;
  traceUrl?: string;
  filePath: string;
  lineNumber?: number;
}

interface TestStats {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  running: number;
  lastRun: string;
  runtime: string;
}

export function E2EStatusMenu() {
  const [stats, setStats] = useState<TestStats | null>(null);
  const [failedTests, setFailedTests] = useState<TestFailure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTest, setSelectedTest] = useState<TestFailure | null>(null);
  const [expandedSpecs, setExpandedSpecs] = useState<Set<string>>(new Set());
  const [pluginEnabled, setPluginEnabled] = useState<boolean | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/e2e-status');

      if (!response.ok) {
        // If the endpoint doesn't exist, the plugin is disabled
        if (response.status === 404) {
          setPluginEnabled(false);
          return;
        }
        throw new Error(`API error: ${response.status}`);
      }

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Not JSON response, plugin is probably disabled or endpoint not available
        setPluginEnabled(false);
        return;
      }

      const data = await response.json();

      setPluginEnabled(true);
      setStats(data.stats);
      setFailedTests(data.failedTests);

      // Auto-select first test for better UX
      if (data.failedTests.length > 0 && !selectedTest) {
        setSelectedTest(data.failedTests[0]);
      }
    } catch (err) {
      console.error('E2E Status error:', err);
      // Network errors or fetch failures also indicate plugin is disabled
      setPluginEnabled(false);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 180000);
    return () => clearInterval(interval);
  }, []);

  const handleAction = async (action: string, test: TestFailure) => {
    try {
      switch (action) {
        case 'currents':
          const traceUrl = test.traceUrl || test.instanceUrl + '/trace';
          window.open(traceUrl, '_blank');
          break;
        case 'edit':
          await fetch('/api/e2e-edit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filePath: test.filePath }),
          });
          break;
        case 'claude':
          await fetch('/api/e2e-claude', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ test }),
          });
          break;
        case 'copy':
          await navigator.clipboard.writeText(test.filePath);
          break;
      }
    } catch (err) {
      console.error('Action failed:', err);
    }
  };

  // Don't render if plugin is disabled
  if (pluginEnabled === false) {
    return null;
  }

  // Don't show error state if we're still checking for plugin
  if (pluginEnabled === null && isLoading) {
    return null;
  }

  if (!stats && !error && isLoading) {
    return (
      <div className="fixed right-5 bottom-5 z-50 rounded-lg border border-gray-600 bg-gray-800/95 px-3 py-2 text-sm text-gray-100 backdrop-blur">
        Loading E2E Status...
      </div>
    );
  }

  // Only show error if plugin is explicitly enabled but there's an API error
  if (error && pluginEnabled === true) {
    return (
      <div className="fixed right-5 bottom-5 z-50 rounded-lg border border-red-600 bg-red-800/95 px-3 py-2 text-sm text-red-100 backdrop-blur">
        ❌ {error}
      </div>
    );
  }

  if (!stats) return null;

  const statusText =
    [
      stats.failed > 0 ? `🔴${stats.failed}` : '',
      stats.passed > 0 ? `🟢${stats.passed}` : '',
      stats.running > 0 ? `🔵${stats.running}` : '',
    ]
      .filter(Boolean)
      .join(' ') || '❓';

  const passRate =
    stats.total > 0 ? Math.round((stats.passed / stats.total) * 100) : 0;

  // Categorize tests
  const uiTests = failedTests.filter(
    test =>
      !test.spec.includes('expected-vulnerabilities') &&
      !test.spec.includes('capabilities')
  );
  const capabilityTests = failedTests.filter(
    test =>
      test.spec.includes('expected-vulnerabilities') ||
      test.spec.includes('capabilities')
  );

  // Group tests by spec file
  const groupTestsBySpec = (tests: TestFailure[]) => {
    const grouped = new Map<string, TestFailure[]>();
    tests.forEach(test => {
      const existing = grouped.get(test.spec) || [];
      grouped.set(test.spec, [...existing, test]);
    });
    return grouped;
  };

  const uiTestsBySpec = groupTestsBySpec(uiTests);
  const capabilityTestsBySpec = groupTestsBySpec(capabilityTests);

  const toggleSpec = (spec: string) => {
    const newExpanded = new Set(expandedSpecs);
    if (newExpanded.has(spec)) {
      newExpanded.delete(spec);
    } else {
      newExpanded.add(spec);
    }
    setExpandedSpecs(newExpanded);
  };

  return (
    <div className="fixed right-5 bottom-5 z-50">
      {/* Status Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-lg border border-gray-600 bg-gray-800/95 px-3 py-2 text-sm font-medium text-gray-100 shadow-lg backdrop-blur transition-all duration-200 hover:bg-gray-700/95"
      >
        {statusText}
      </button>

      {/* Full-Width Two-Panel Modal */}
      {isOpen && (
        <div className="bg-chariot-background-primary border-chariot-stroke fixed inset-4 z-50 flex flex-col rounded-lg border shadow-2xl">
          {/* Header */}
          <div className="border-chariot-stroke flex items-center justify-between border-b p-4">
            <div>
              <h2 className="text-chariot-text-primary text-lg font-semibold">
                E2E Test Failures
              </h2>
              <div className="text-chariot-text-secondary text-sm">
                {passRate}% pass rate ({stats.failed} total failures) • Last
                run: {stats.lastRun} ({stats.runtime})
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-chariot-text-secondary hover:text-chariot-text-primary"
            >
              <X size={20} />
            </button>
          </div>

          {/* Two-Panel Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Panel: Test List */}
            <div className="border-chariot-stroke flex w-1/2 flex-col border-r">
              <div className="border-chariot-stroke border-b p-3">
                <h3 className="text-chariot-text-primary text-sm font-medium">
                  Failed Tests ({failedTests.length})
                </h3>
              </div>

              <div className="flex-1 overflow-y-auto">
                {/* UI Test Failures */}
                {uiTests.length > 0 && (
                  <div>
                    <div className="bg-chariot-background-secondary text-chariot-text-secondary border-chariot-stroke border-b px-3 py-2 text-xs font-semibold">
                      UI Test Failures ({uiTests.length})
                    </div>
                    {Array.from(uiTestsBySpec.entries()).map(
                      ([spec, tests]) => (
                        <div key={spec}>
                          {/* Accordion Header */}
                          <div
                            onClick={() => toggleSpec(spec)}
                            className="bg-chariot-background-tertiary border-chariot-stroke hover:bg-chariot-stroke flex cursor-pointer items-center justify-between border-b px-3 py-2"
                          >
                            <div className="flex items-center gap-2">
                              {expandedSpecs.has(spec) ? (
                                <ChevronDown
                                  size={16}
                                  className="text-chariot-text-secondary"
                                />
                              ) : (
                                <ChevronRight
                                  size={16}
                                  className="text-chariot-text-secondary"
                                />
                              )}
                              <span className="text-chariot-text-primary text-sm font-medium">
                                {spec}
                              </span>
                              <span className="text-chariot-text-secondary text-xs">
                                ({tests.length})
                              </span>
                            </div>
                          </div>

                          {/* Accordion Content */}
                          {expandedSpecs.has(spec) &&
                            tests.map(test => (
                              <div
                                key={test.id}
                                onClick={() => setSelectedTest(test)}
                                className={`border-chariot-stroke cursor-pointer border-b px-3 py-2 pl-9 text-sm transition-colors ${
                                  selectedTest?.id === test.id
                                    ? 'bg-chariot-stroke text-chariot-text-primary'
                                    : 'text-chariot-text-secondary hover:bg-chariot-stroke'
                                }`}
                              >
                                <div className="font-medium">
                                  {test.testName}
                                </div>
                                <div className="mt-1 truncate text-xs opacity-75">
                                  {test.error}
                                </div>
                              </div>
                            ))}
                        </div>
                      )
                    )}
                  </div>
                )}

                {/* Capability Failures */}
                {capabilityTests.length > 0 && (
                  <div>
                    <div className="bg-chariot-background-secondary text-chariot-text-secondary border-chariot-stroke border-b px-3 py-2 text-xs font-semibold">
                      Capability Failures ({capabilityTests.length})
                    </div>
                    {Array.from(capabilityTestsBySpec.entries()).map(
                      ([spec, tests]) => (
                        <div key={spec}>
                          {/* Accordion Header */}
                          <div
                            onClick={() => toggleSpec(spec)}
                            className="bg-chariot-background-tertiary border-chariot-stroke hover:bg-chariot-stroke flex cursor-pointer items-center justify-between border-b px-3 py-2"
                          >
                            <div className="flex items-center gap-2">
                              {expandedSpecs.has(spec) ? (
                                <ChevronDown
                                  size={16}
                                  className="text-chariot-text-secondary"
                                />
                              ) : (
                                <ChevronRight
                                  size={16}
                                  className="text-chariot-text-secondary"
                                />
                              )}
                              <span className="text-chariot-text-primary text-sm font-medium">
                                {spec}
                              </span>
                              <span className="text-chariot-text-secondary text-xs">
                                ({tests.length})
                              </span>
                            </div>
                          </div>

                          {/* Accordion Content */}
                          {expandedSpecs.has(spec) &&
                            tests.map(test => {
                              const cleanName = test.testName.includes(
                                'vulnerability:'
                              )
                                ? test.testName
                                    .split('vulnerability:')[1]
                                    ?.trim()
                                    .replace(' freshness', '') || test.testName
                                : test.testName;
                              return (
                                <div
                                  key={test.id}
                                  onClick={() => setSelectedTest(test)}
                                  className={`border-chariot-stroke cursor-pointer border-b px-3 py-2 pl-9 text-sm transition-colors ${
                                    selectedTest?.id === test.id
                                      ? 'bg-chariot-stroke text-chariot-text-primary'
                                      : 'text-chariot-text-secondary hover:bg-chariot-stroke'
                                  }`}
                                >
                                  <div className="font-medium">{cleanName}</div>
                                  <div className="mt-1 truncate text-xs opacity-75">
                                    {test.error}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel: Test Details */}
            <div className="flex w-1/2 flex-col">
              {selectedTest ? (
                <>
                  {/* Test Info Header */}
                  <div className="border-chariot-stroke border-b p-4">
                    <h3 className="text-chariot-text-primary mb-2 text-lg font-medium">
                      {selectedTest.testName}
                    </h3>
                    <div className="text-chariot-text-secondary space-y-1 text-sm">
                      <div>
                        <strong>Spec:</strong> {selectedTest.spec}
                      </div>
                      <div>
                        <strong>File:</strong> {selectedTest.filePath}
                      </div>
                      {selectedTest.lineNumber &&
                        selectedTest.lineNumber > 1 && (
                          <div>
                            <strong>Line:</strong> {selectedTest.lineNumber}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Comprehensive Error Details */}
                  <div className="border-chariot-stroke flex-1 overflow-y-auto border-b p-4">
                    <h4 className="text-chariot-text-secondary mb-3 text-sm font-medium">
                      Error Details
                    </h4>

                    <div className="space-y-4">
                      {/* Main Error Details */}
                      <ErrorDisplay content={selectedTest.fullError} />

                      {/* Stack Trace */}
                      {selectedTest.stackTrace &&
                        selectedTest.stackTrace !==
                          'No stack trace available' && (
                          <ErrorDisplay content={selectedTest.stackTrace} />
                        )}

                      {/* Code Frame (if available) */}
                      {selectedTest.errorDetails?.codeFrame && (
                        <div>
                          <div className="text-chariot-text-primary mb-2 text-xs font-medium">
                            Code Context:
                          </div>
                          <div className="bg-chariot-background-tertiary border-chariot-stroke overflow-hidden rounded-lg border">
                            <div className="bg-chariot-background-secondary border-chariot-stroke border-b px-3 py-2">
                              <div className="text-chariot-text-secondary text-xs font-medium">
                                Source Code
                              </div>
                            </div>
                            <div className="p-4">
                              <pre className="text-chariot-text-accent font-mono text-xs whitespace-pre-wrap">
                                {JSON.stringify(
                                  selectedTest.errorDetails.codeFrame,
                                  null,
                                  2
                                )}
                              </pre>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Test Attempts (if multiple) */}
                      {selectedTest.errorDetails?.attempts?.length > 1 && (
                        <div>
                          <div className="text-chariot-text-primary mb-2 text-xs font-medium">
                            Test Attempts (
                            {selectedTest.errorDetails.attempts.length}):
                          </div>
                          <div className="space-y-2">
                            {selectedTest.errorDetails.attempts.map(
                              (attempt: any, index: number) => (
                                <div
                                  key={index}
                                  className="border-chariot-stroke rounded border p-2 text-xs"
                                >
                                  <div className="text-chariot-text-primary font-medium">
                                    Attempt {index + 1}
                                  </div>
                                  <div className="text-chariot-text-primary">
                                    <div>
                                      State:{' '}
                                      <span
                                        className={
                                          attempt.state === 'passed'
                                            ? 'text-chariot-stroke-info'
                                            : 'text-chariot-text-red'
                                        }
                                      >
                                        {attempt.state}
                                      </span>
                                    </div>
                                    <div>
                                      Duration:{' '}
                                      {attempt.wallClockDuration?.toLocaleString() ||
                                        0}
                                      ms
                                    </div>
                                    {attempt.error && (
                                      <div className="text-chariot-text-red mt-1">
                                        Error: {attempt.error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* Annotations (if any) */}
                      {selectedTest.errorDetails?.annotations?.length > 0 && (
                        <div>
                          <div className="text-chariot-text-primary mb-2 text-xs font-medium">
                            Annotations:
                          </div>
                          <div className="space-y-1">
                            {selectedTest.errorDetails.annotations.map(
                              (annotation: any, index: number) => (
                                <div
                                  key={index}
                                  className="rounded border border-blue-500/30 bg-blue-900/20 p-2 text-xs text-blue-300"
                                >
                                  <div className="font-medium">
                                    {annotation.type}
                                  </div>
                                  <div>{annotation.description}</div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="border-chariot-stroke border-t p-4">
                    <h4 className="text-chariot-text-primary mb-3 text-sm font-medium">
                      Actions
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => handleAction('currents', selectedTest)}
                        styleType="rounded"
                        size="sm"
                        startIcon={<Play size={14} />}
                        label="View Trace"
                      />
                      <Button
                        onClick={() => handleAction('edit', selectedTest)}
                        styleType="rounded"
                        size="sm"
                        startIcon={<Pencil size={14} />}
                        label="Edit Spec"
                      />
                      <Button
                        onClick={() => handleAction('claude', selectedTest)}
                        styleType="rounded"
                        size="sm"
                        startIcon={<Bot size={14} />}
                        label="Fix with Claude"
                      />
                      <Button
                        onClick={() => handleAction('copy', selectedTest)}
                        styleType="rounded"
                        size="sm"
                        startIcon={<Copy size={14} />}
                        label="Copy Path"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-chariot-text-secondary flex flex-1 items-center justify-center">
                  <div className="text-center">
                    <div className="mb-4 text-4xl">🧪</div>
                    <div className="mb-2 text-lg font-medium">
                      Select a test
                    </div>
                    <div className="text-sm">
                      Choose a failed test from the left to view details and
                      actions
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-chariot-stroke flex items-center justify-between border-t p-3">
            <button
              onClick={() => window.open('https://app.currents.dev', '_blank')}
              className="text-chariot-brand-primary text-sm font-semibold hover:underline"
            >
              Open Currents Dashboard
            </button>
            <button
              onClick={loadData}
              className="text-chariot-brand-primary text-sm font-semibold hover:underline"
            >
              Refresh Data
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
